import { useCallback, useEffect, useRef, useState } from "react";

import * as Cmp from "@impero-com/common/utils/comparison";
import { getParentElement } from "@impero-com/common/utils/dom";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { Button, FormField, FormLabel, Svg, Tooltip } from "@impero-com/ui";
import RemoveIconSvg from "@impero-com/ui/img/remove.svg";
import { Select, Typography } from "antd";

import { EntityOverview, EntityView } from "api/generated/model";
import { BaseFormDisplay } from "components/form/legacyFields/formDisplay";
import { useControlProgramQuery } from "org/control/controlProgram/queries/useControlProgramQuery";
import { useEntitiesQuery } from "org/entities/queries/useEntitiesQuery";

import * as theme from "./theme.css";

const { Text } = Typography;

interface EntitySectionProps {
    controlProgramId: number | null;
    entityId: number | undefined;
    onEntityChanged: (entityId: number | undefined) => void;
    onValidationChange?: (error: string | undefined) => void;
}

export function EntitySection({
    controlProgramId,
    entityId,
    onEntityChanged,
    onValidationChange,
}: EntitySectionProps) {
    if (controlProgramId === null) {
        return <EntitySectionDisabled />;
    }
    return (
        <EntitySectionEdit
            controlProgramId={controlProgramId}
            entityId={entityId}
            onEntityChanged={onEntityChanged}
            onValidationChange={onValidationChange}
        />
    );
}

function EntitySectionDisabled() {
    const { t } = useTranslation();
    return (
        <BaseFormDisplay label={t("AccessAndAssignment_Entities_Entity")}>
            <Tooltip
                title={t("Control_Assignment_ControlProgram_NotAvailable")}
            >
                <Select
                    placeholder={t("Control_Assignment_Entity_Placeholder")}
                    style={{ minWidth: 300 }}
                    disabled={true}
                />
            </Tooltip>
        </BaseFormDisplay>
    );
}

interface EntitySectionEditProps extends EntitySectionProps {
    controlProgramId: number;
}

function EntitySectionEdit({
    controlProgramId,
    entityId,
    onEntityChanged,
    onValidationChange,
}: EntitySectionEditProps) {
    const { t } = useTranslation();
    const { data } = useControlProgramQuery(controlProgramId);
    const linkedEntities: EntityView[] = data.linkedResources.entities;
    const { data: entities } = useEntitiesQuery({
        all: false,
        scope: "control",
    });

    // TODO RFC 24, this has to change in favour of checks on the control itself.
    const hasFullAccessToProgram =
        data.content.actions.edit.accessRights ||
        data.content.actions.edit.content;

    const entitiesWithEditAccess: EntityOverview[] = entities.content
        .filter((item) => item.actions.control.editAssignment)
        .map((item) => item.resource);

    const mergedEntities = linkedEntities.filter((linkedEntity) =>
        entitiesWithEditAccess.some(
            (accessibleEntity) => accessibleEntity.id === linkedEntity.id
        )
    );

    const defaultValue = getDefaultEntityId(
        hasFullAccessToProgram,
        entityId,
        mergedEntities
    );

    const [selectedEntityId, setSelectedEntityId] = useState<
        number | undefined
    >(defaultValue);

    const options = getLinkedEntityOptions(
        hasFullAccessToProgram,
        linkedEntities,
        entitiesWithEditAccess
    );

    const disabled = linkedEntities.length === 0;
    const disabledTooltip = entitiesWithEditAccess.length >= 1 && !disabled;
    const isEntityRequired =
        !hasFullAccessToProgram && options.length >= 1 && !selectedEntityId;
    const validationMessage = isEntityRequired
        ? t("Control_Assignment_Entity_Required")
        : undefined;

    // Tracks the entity we last propagated to the parent, so we can skip any
    // call that would re-emit a change event for an already-selected entity
    const notifiedEntityIdRef = useRef<number | undefined>(entityId);

    const handleEntityChange = useCallback(
        // eslint-disable-next-line react-hooks/preserve-manual-memoization
        (nextEntityId: number | undefined) => {
            if (notifiedEntityIdRef.current === nextEntityId) {
                return;
            }
            notifiedEntityIdRef.current = nextEntityId;
            setSelectedEntityId(nextEntityId);
            onEntityChanged(nextEntityId);
        },
        [onEntityChanged]
    );

    useEffect(() => {
        if (defaultValue !== undefined) {
            handleEntityChange(defaultValue);
        }
        onValidationChange?.(validationMessage);
    }, [
        defaultValue,
        handleEntityChange,
        onValidationChange,
        validationMessage,
    ]);

    const disabledRemoveButton = disabled || !selectedEntityId;

    const selectedEntityName = linkedEntities.find(
        (entity) => entity.id === selectedEntityId
    )?.name;

    return (
        <FormField>
            <FormLabel>
                {t("AccessAndAssignment_Entities_Entity")}
                {isEntityRequired && (
                    <span className={theme.requiredStar}> *</span>
                )}
            </FormLabel>
            <Tooltip
                title={t("Control_Assignment_Entities_NotAvailable")}
                disabled={disabledTooltip}
            >
                <div className={theme.entitySelectContainer}>
                    <Select
                        options={options}
                        defaultValue={selectedEntityId}
                        showSearch
                        optionFilterProp="label"
                        autoFocus
                        placeholder={t("Control_Assignment_Entity_Placeholder")}
                        style={{ minWidth: 300 }}
                        onChange={handleEntityChange}
                        disabled={disabled}
                        value={selectedEntityId}
                        // Render the dropdown inside the drawer's scroll
                        // container so it stays anchored to the input
                        getPopupContainer={getParentElement}
                    />
                    {hasFullAccessToProgram && (
                        <Button
                            disabled={disabledRemoveButton}
                            variant="link"
                            onClick={() => handleEntityChange(undefined)}
                            className={
                                disabledRemoveButton
                                    ? theme.disabledRemoveButton
                                    : theme.removeButton
                            }
                        >
                            <Svg
                                className={theme.removeIcon}
                                src={RemoveIconSvg}
                            />
                        </Button>
                    )}
                </div>
            </Tooltip>
            {selectedEntityName && (
                <Text type="secondary" className={theme.entityTagsCaption}>
                    {t("Control_Assignment_Entity_Tags_Caption", {
                        entity: selectedEntityName,
                    })}
                </Text>
            )}
        </FormField>
    );
}

const getDefaultEntityId = (
    hasFullAccessToProgram: boolean,
    entityId: number | undefined,
    availableEntities: EntityView[]
): number | undefined => {
    if (entityId) {
        return entityId;
    }
    if (!hasFullAccessToProgram && availableEntities.length === 1) {
        return availableEntities[0].id;
    }
    return undefined;
};

interface EntityOptions {
    label: string;
    value: number;
}

const getLinkedEntityOptions = (
    hasFullAccessToProgram: boolean,
    linkedEntities: EntityView[],
    entitiesWithEditAccess: EntityOverview[]
): EntityOptions[] => {
    const cmp = Cmp.map((option: EntityOptions) => option.label, Cmp.string());

    if (hasFullAccessToProgram) {
        return linkedEntities
            .map((entity) => ({
                label: entity.name,
                value: entity.id,
            }))
            .sort(cmp);
    } else {
        return linkedEntities
            .filter((entity) =>
                entitiesWithEditAccess.some(
                    (accessibleEntity) => accessibleEntity.id === entity.id
                )
            )
            .map((entity) => ({
                label: entity.name,
                value: entity.id,
            }))
            .sort(cmp);
    }
};
